// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Normalizes any thrown value into a user-safe, friendly error.
 *
 * The backend (see DTCCAPP-2053) sanitizes error payloads and injects a
 * `trace_id`. Error bodies follow the RFC-6749 shape
 * `{ error, error_description, trace_id? }`. This util parses that shape out of
 * the generated `ApiException` wrappers and produces a
 * {@link FriendlyError} that the UI can render without ever leaking stack
 * traces, raw JSON, or internal identifiers.
 *
 * Detection is structural (duck typing) rather than `instanceof`, because each
 * generated OpenAPI client ships its own copy of the `ApiException` class, so a
 * single `instanceof` check is not reliable across clients.
 */

const UNEXPECTED_MESSAGE = 'An unexpected error occurred.';

/**
 * Compose the support-facing message for a system error carrying a reference id.
 * Used where the trace id must be inline (e.g. a single snackbar line).
 */
export const referenceMessage = (traceId: string): string =>
  `${UNEXPECTED_MESSAGE} Please contact support with Reference ID: ${traceId}`;

/** A user-facing error, safe to display. Contains no internal system detail. */
export interface FriendlyError {
  /** Human-readable message to show to the user. */
  message: string;
  /** Reference/Trace ID to quote to support, when the failure is a system error. */
  traceId?: string;
  /**
   * True when this originated from a known domain/business rule (the backend
   * message is meaningful to the user); false for unexpected/system failures.
   */
  isDomain: boolean;
}

/** Sanitized error body shape returned by the backend. */
interface ApiErrorBody {
  error?: string;
  error_description?: string;
  message?: string;
  trace_id?: string;
}

/**
 * Structural shape of the generated `ApiException` classes. We only rely on the
 * fields we read, so any client's `ApiException` satisfies it.
 */
interface ApiExceptionLike {
  code: number;
  body: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isApiExceptionLike = (value: unknown): value is ApiExceptionLike =>
  isRecord(value) && typeof value.code === 'number' && 'body' in value;

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const readBody = (body: unknown): ApiErrorBody | undefined => {
  if (isRecord(body)) return body as ApiErrorBody;
  if (typeof body === 'string') {
    try {
      const parsed: unknown = JSON.parse(body);
      return isRecord(parsed) ? (parsed as ApiErrorBody) : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

/**
 * Parse any thrown value into a {@link FriendlyError}.
 *
 * - Sanitized API errors with a `trace_id` are treated as system failures and
 *   surface the reference-id fallback message.
 * - API errors carrying a clean domain `error_description`/`message` (and no
 *   trace id) surface that message directly.
 * - Anything else falls back to a generic message; a plain `Error.message` is
 *   used only when it is a safe, non-serialized string.
 *
 * @param fallbackMessage caller-supplied context used only for the generic
 *   (non-domain, non-trace-id) case; defaults to a generic message.
 */
export const parseApiError = (error: unknown, fallbackMessage?: string): FriendlyError => {
  const genericMessage = nonEmptyString(fallbackMessage) ?? UNEXPECTED_MESSAGE;

  if (isApiExceptionLike(error)) {
    const body = readBody(error.body);
    const traceId = nonEmptyString(body?.trace_id);
    const domainMessage = nonEmptyString(body?.error_description) ?? nonEmptyString(body?.message);

    // A trace id marks an unexpected/system failure: never show the raw domain
    // string, only the reference-id fallback.
    if (traceId) {
      return { message: referenceMessage(traceId), traceId, isDomain: false };
    }

    if (domainMessage) {
      return { message: domainMessage, isDomain: true };
    }

    return { message: genericMessage, isDomain: false };
  }

  // Plain Error: only trust the message if it is not a serialized blob. The
  // generated ApiException.message embeds "HTTP-Code:/Body:/Headers:" JSON, so
  // guard against leaking that if such an Error slips through un-wrapped.
  if (error instanceof Error) {
    const msg = nonEmptyString(error.message);
    if (msg && !/HTTP-Code:|\bBody:|\bHeaders:|[{[]/.test(msg)) {
      return { message: msg, isDomain: true };
    }
  }

  return { message: genericMessage, isDomain: false };
};

const FRIENDLY_KEY = '__friendlyError';

/**
 * Attach a {@link FriendlyError} onto the `Error` object propagated through
 * `enqueueError`, so the rendering layer can display it without re-parsing.
 * Keeps the existing `enqueueError(error: Error)` contract unchanged.
 */
export const withFriendlyError = (err: Error, friendly: FriendlyError): Error => {
  Object.defineProperty(err, FRIENDLY_KEY, {
    value: friendly,
    enumerable: false,
    configurable: true,
    writable: true,
  });
  return err;
};

/**
 * Read a previously attached {@link FriendlyError}, falling back to parsing the
 * error's `cause` (or the error itself) when none was attached.
 */
export const getFriendlyError = (err: unknown): FriendlyError => {
  if (isRecord(err) && isRecord(err[FRIENDLY_KEY])) {
    return err[FRIENDLY_KEY] as unknown as FriendlyError;
  }
  if (err instanceof Error && err.cause !== undefined) {
    return parseApiError(err.cause);
  }
  return parseApiError(err);
};
